// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, CircularProgress } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState, useRef } from 'react';
// components
import Iconify from 'src/components/iconify';
import { getRequest, postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import useSoftwareDevelopmentSection from 'src/hooks/use-software-development';
// import useSoftwareDevelopmentSection from 'src/hooks/use-software-development';

// ----------------------------------------------------------------------

export default function SoftwareDevelopment() {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const lastWhyChooseRef = useRef(null);
  const lastServicesRef = useRef(null);

  const softwareDevelopmentPageData = useSelector((state) => state.website?.softwareDevelopmentPage);
  const { fetchOnMount } = useSoftwareDevelopmentSection();

  const [formData, setFormData] = useState({
    // Main Header
    mainTitle: "",
    mainTitleColor: "#000000",
    subtitle: "",
    subtitleColor: "#000000",

    // Main Description
    mainDescription: "",
    mainDescriptionColor: "#666666",

    // Why Choose EzeLogs Section
    whyChooseTitle: "",
    whyChooseTitleColor: "#000000",
    whyChooseReasons: [
      {
        text: "",
        textColor: "#666666"
      },
    ],

    // Our Services Section
    servicesTitle: "",
    servicesTitleColor: "#000000",
    services: [
      {
        text: "",
        textColor: "#666666"
      },
    ]
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateListItem = (section, itemIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) =>
        i === itemIndex ? { ...item, [field]: value } : item
      )
    }));
  };

  const addListItem = (section, refToScroll) => {
    const newItem = {
      text: "",
      textColor: "#666666"
    };

    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], newItem]
    }));

    setTimeout(() => {
      if (refToScroll && refToScroll.current) {
        refToScroll.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const removeListItem = (section, itemIndex) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== itemIndex)
    }));
  };

  useEffect(() => {
    fetchOnMount();
  }, [fetchOnMount]);

  useEffect(() => {
    if (softwareDevelopmentPageData?.software_development_section) {
      // Create a deep copy to avoid mutating the read-only Redux store data
      const deepCopy = JSON.parse(JSON.stringify(softwareDevelopmentPageData.software_development_section));
      setFormData(prev => ({
        ...prev,
        ...deepCopy
      }));
    }
  }, [softwareDevelopmentPageData]);

  const handleSave = async () => {
    setLoading(true);

       try {
         // Create a FormData object for file uploads
         const formDataToSend = new FormData();

         // Append all form data
         Object.entries(formData).forEach(([key, value]) => {
           // Skip heroImage as we'll handle it separately
           if (key !== 'heroImage') {
             // Handle nested objects like primaryButton and secondaryButton
             if (typeof value === 'object' && value !== null && !(value instanceof File)) {
               formDataToSend.append(key, JSON.stringify(value));
             } else {
               formDataToSend.append(key, value);
             }
           }
         });

         // Append the hero image if it exists
         if (formData.heroImage) {
           formDataToSend.append('heroImage', formData.heroImage);
         }

         const response = await postRequestForWebsite(
           endpoints.admin_dashboard.website?.software_development_page?.software_development_section,
           // formDataToSend,
           // dataForHeroSection,
           {
             attributes: {
               ...JSON.parse(JSON.stringify(softwareDevelopmentPageData)),
               software_development_section: JSON.parse(JSON.stringify(formData))
             }
           },
           true
         );

         if (response.metadata?.success) {
           enqueueSnackbar({
             variant: 'success',
             message: response.metadata.message,
           });
         } else {
           handleErrorMessages(response.message || 'An error occurred.');
         }
       } catch (err) {
         console.error('Error:', err);
         handleErrorMessages(err.message || 'Submission failed.');
       } finally {
         setLoading(false);
       }
  };

  const renderListSection = (sectionTitle, titleKey, titleColorKey, listKey, items, refToScroll) => (
    <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center">
          <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
          <Typography variant="h6">{sectionTitle}</Typography>
        </Stack>
        <Button
          variant="contained"
          color="success"
          startIcon={<Iconify icon="mingcute:add-line" />}
          onClick={() => addListItem(listKey, refToScroll)}
        >
          Add Item
        </Button>
      </Stack>

      {/* Section Title */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={10}>
          <TextField
            fullWidth
            label={`${sectionTitle} Title`}
            value={formData[titleKey]}
            onChange={(e) => updateFormData(titleKey, e.target.value)}
            placeholder={`Enter ${sectionTitle.toLowerCase()} title`}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Title Color"
            type="color"
            value={formData[titleColorKey]}
            onChange={(e) => updateFormData(titleColorKey, e.target.value)}
            sx={{ '& input': { height: 20 } }}
          />
        </Grid>
      </Grid>

      {/* List Items */}
      <Grid container spacing={2}>
        {items.map((item, itemIndex) => (
          <Grid item xs={12} key={itemIndex}>
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: 'background.neutral' }}
              ref={itemIndex === items.length - 1 ? refToScroll : null}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Typography variant="caption" color="primary">
                  Item {itemIndex + 1}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => removeListItem(listKey, itemIndex)}
                  sx={{ minWidth: 25, p: 0.2 }}
                >
                  ×
                </Button>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={10}>
                  <TextField
                    fullWidth
                    label="Item Text"
                    multiline
                    rows={3}
                    value={item.text}
                    onChange={(e) => updateListItem(listKey, itemIndex, 'text', e.target.value)}
                    placeholder="Enter item text"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Text Color"
                    type="color"
                    value={item.textColor}
                    onChange={(e) => updateListItem(listKey, itemIndex, 'textColor', e.target.value)}
                    sx={{ '& input': { height: 20 } }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Software Development
        </Typography>

        {/* Main Header Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Main Header</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Main Title"
                value={formData.mainTitle}
                onChange={(e) => updateFormData('mainTitle', e.target.value)}
                placeholder="Enter main title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.mainTitleColor}
                onChange={(e) => updateFormData('mainTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Subtitle"
                multiline
                rows={2}
                value={formData.subtitle}
                onChange={(e) => updateFormData('subtitle', e.target.value)}
                placeholder="Enter subtitle"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Subtitle Color"
                type="color"
                value={formData.subtitleColor}
                onChange={(e) => updateFormData('subtitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Main Description Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Main Description</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Main Description"
                multiline
                rows={4}
                value={formData.mainDescription}
                onChange={(e) => updateFormData('mainDescription', e.target.value)}
                placeholder="Enter main description"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Text Color"
                type="color"
                value={formData.mainDescriptionColor}
                onChange={(e) => updateFormData('mainDescriptionColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Why Choose EzeLogs Section */}
        {renderListSection("Why Choose EzeLogs", "whyChooseTitle", "whyChooseTitleColor", "whyChooseReasons", formData.whyChooseReasons, lastWhyChooseRef)}

        {/* Our Services Section */}
        {renderListSection("Our Services", "servicesTitle", "servicesTitleColor", "services", formData.services, lastServicesRef)}

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
